import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { MicrosoftLoginGuard } from './microsoft-login.guard';
import { MobileLoginGuard } from './mobile-login.guard';

import { createLogResolver } from '@core/logs/resolvers/create-log.resolver';

import { HomeComponent } from '../app/home/pages/home/home.component';
import { LoginComponent } from '../app/pages/login/login.component';
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { MenuConfigComponent } from './pages/menu-config/menu-config.component';
import { RequestSetupComponent } from './pages/request-setup/request-setup.component';

import { UrlNotFoundComponent } from './components/url-not-found/url-not-found.component';
import { Utils } from '../app/utils/utils';

// Menu items
import { menuItems } from './menu-items';

const userAgent = navigator.userAgent;

const routes: Routes = [
  {
    path: '',
    component: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? LoginComponent
      : LoginComponent,
    pathMatch: 'full'
  },
  {
    path: 'login',
    component: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? LoginComponent
      : LoginComponent
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard]
  },
  {
    path: 'teams-setup',
    component: RequestSetupComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Teams Setup',
      required: Utils.filterRequiredByLink(menuItems, 'teams-setup')
    }
  },
  {
    path: 'users-setup',
    component: RequestSetupComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Users Setup',
      required: Utils.filterRequiredByLink(menuItems, 'users-setup')
    }
  },
  {
    path: 'roles-setup',
    component: RequestSetupComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Roles Setup',
      required: Utils.filterRequiredByLink(menuItems, 'roles-setup')
    }
  },
  {
    path: 'task-management/request-setup',
    component: RequestSetupComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Request Type Setup',
      required: Utils.filterRequiredByLink(menuItems, 'task-management/request-setup')
    }
  },
  {
    path: 'task-management/priority-setup',
    component: RequestSetupComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Priority Setup',
      required: Utils.filterRequiredByLink(menuItems, 'task-management/priority-setup')
    }
  },
  /* Vendors-svm pages */

  /* Skills Setup */
  {
    path: 'skills-management/skill-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/skills-center/skills-center.component').then((m) => m.SkillsCenterComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Skill Setup',
      required: Utils.filterRequiredByLink(menuItems, 'skills-management/skill-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /* Skills Setup END */
  /* Service Setup */
  {
    path: 'services-management/service-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/services-center/services-center.component').then((m) => m.ServicesCenterComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Service Setup',
      required: Utils.filterRequiredByLink(menuItems, 'services-management/service-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /* Service Setup END*/
  /* Technology Setup */
  {
    path: 'technologies-management/technology-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/technologies-center/technologies-center.component').then(
        (m) => m.TechnologiesCenterComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Technology Setup',
      required: Utils.filterRequiredByLink(menuItems, 'technologies-management/technology-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /* Technology Setup END*/
  /* Certifications Setup */
  {
    path: 'certifications-management/certification-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/certifications-center/certifications-center.component').then(
        (m) => m.CertificationsCenterComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Certification Setup',
      required: Utils.filterRequiredByLink(menuItems, 'certifications-management/certification-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /* Certifications Setup END*/
  {
    path: 'vendors-management/vendor-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/vendors-center/vendors-center.component').then((m) => m.VendorsCenterComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Vendor Setup',
      required: Utils.filterRequiredByLink(menuItems, 'vendors-management/vendor-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'vendors-management/vendor-dashboard',
    loadComponent: () =>
      import('@vendor-svm/pages/vendors-dashboard/vendors-dashboard.component').then(
        (m) => m.VendorsDashboardComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Service Vendors  Dashboard',
      required: Utils.filterRequiredByLink(menuItems, 'vendors-management/vendor-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /* Vendors END */

  /**** Start PAC40 *****/
  {
    path: 'pac-forty/pac-forty-dashboard',
    loadComponent: () =>
      import('@pac-forty/homepage-dashboard/pages/pac-forty-dashboard/pac-forty-dashboard.component').then(
        (m) => m.PacFortyDashboardComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/sales-dashboard/:id',
    loadComponent: () =>
      import('@pac-forty/sales-dashboard/pages/pac-forty-sales-dashboard/pac-forty-sales-dashboard.component').then(
        (m) => m.PacFortySalesDashboardComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Sales Dashboard',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/operations-dashboard/:id',
    loadComponent: () =>
      import(
        '@pac-forty/operations-dashboard/pages/pac-forty-operations-dashboard/pac-forty-operations-dashboard.component'
      ).then((m) => m.PacFortyOperationsDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Operations Dashboard',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/client-ranking',
    loadComponent: () =>
      import(
        '@pac-forty/client-ranking-dashboard/pages/pac-forty-client-ranking-dashboard/pac-forty-client-ranking-dashboard.component'
      ).then((m) => m.PacFortyClientRankingDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Client Ranking',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/pipeline-overview',
    loadComponent: () =>
      import(
        '@pac-forty/pipeline-overview-dashboard/pages/pac-forty-pipeline-overview-dashboard/pac-forty-pipeline-overview-dashboard.component'
      ).then((m) => m.PacFortyPipelineOverviewDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Pipeline Overview',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/vendor-ranking',
    loadComponent: () =>
      import(
        '@pac-forty/vendor-ranking-dashboard/pages/pac-forty-vendor-ranking-dashboard/pac-forty-vendor-ranking-dashboard.component'
      ).then((m) => m.PacFortyVendorRankingDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Vendor Ranking',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/pipeline-trend',
    loadComponent: () =>
      import(
        '@pac-forty/pipeline-trend-dashboard/pages/pac-forty-pipeline-trend-dashboard/pac-forty-pipeline-trend-dashboard.component'
      ).then((m) => m.PacFortyPipelineTrendDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Pipeline Trend',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'pac-forty/pipeline-nine-blocks',
    loadComponent: () =>
      import(
        '@pac-forty/nine-blocks-dashboard/pages/pac-forty-nine-blocks-dashboard/pac-forty-nine-blocks-dashboard.component'
      ).then((m) => m.PacFortyNineBlocksDashboardComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PAC 40 / Pipeline 9 Blocks',
      required: Utils.filterRequiredByLink(menuItems, 'pac-forty/pac-forty-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },

  /**** END PAC40 *****/
  /** S2P */
  {
    path: 's2p',
    loadChildren: () => import('@s2p/routes').then((m) => m.S2P_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** My Workspace */
  {
    path: 'my-workspace',
    loadComponent: () =>
      import('@my-workspace/pages/my-workspace/my-workspace.component').then((m) => m.MyWorkspaceComponent),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: { skip: true },
      required: Utils.filterRequiredByLink(menuItems, 'my-workspace')
    },
    loadChildren: () => import('@my-workspace/routes').then((m) => m.MY_WORKSPACE_ROUTES)
  },

  /** Task Management */
  /** New PO Backlog */
  {
    path: 'po-backlog-management',
    loadChildren: () => import('@po-backlog-management/routes').then((m) => m.PO_BACKLOG_MGMT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** PO Backlog Dashboards */
  {
    path: 'po-snapshot-dashboard',
    loadComponent: () =>
      import('@po-backlog/po-snapshot-dashboard/pages/po-snapshot-dashboard/po-snapshot-dashboard.component').then(
        (m) => m.PoSnapshotDashboardComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PO Snapshot Dashboard',
      required: Utils.filterRequiredByLink(menuItems, 'po-snapshot-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'po-backlog-dashboard',
    loadComponent: () =>
      import('@po-backlog/po-backlog-dashboard/pages/po-backlog-dashboard/po-backlog-dashboard.component').then(
        (m) => m.PoBacklogDashboardComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'PO Backlog Dashboard',
      required: Utils.filterRequiredByLink(menuItems, 'po-backlog-dashboard')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /** Ship Preparation */
  {
    path: 'ship-preparation-management',
    loadChildren: () => import('@ship-preparation-management/routes').then((m) => m.SHIP_PREP_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** Renewals*/
  {
    path: 'client-renewals-setup',
    loadComponent: () =>
      import('@renewals/renewals-setup/pages/renewals-setup-center/renewals-setup-center.component').then(
        (m) => m.RenewalsSetupCenterComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Client Renewals Setup',
      required: Utils.filterRequiredByLink(menuItems, 'client-renewals-setup')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'renewals-new-quote',
    loadComponent: () =>
      import('@renewals/renewals-new-quote/pages/renewals-new-quote/renewals-new-quote.component').then(
        (m) => m.RenewalsNewQuoteComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Request a Quote',
      required: Utils.filterRequiredByLink(menuItems, 'renewals-new-quote')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  {
    path: 'renewals-management',
    loadChildren: () => import('@renewals-management/routes').then((m) => m.RENEWALS_MGMT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  {
    path: 'rma',
    loadChildren: () => import('./task-management/rma/routes').then((m) => m.RMA_ROUTES),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** Requests Management */
  {
    path: 'requests-management',
    loadComponent: () =>
      import('@requests-management/pages/requests-mgmt-center/requests-mgmt-center.component').then(
        (m) => m.RequestsMgmtCenterComponent
      ),
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Requests Management',
      required: Utils.filterRequiredByLink(menuItems, 'requests-management')
    },
    resolve: {
      logCreated: createLogResolver
    }
  },
  /** TS Project Management */
  // {
  //   path: 'ts-project',
  //   loadChildren: () => import('./task-management/ts-project/routes').then((m) => m.TS_PROJECT_ROUTES),
  //   canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
  //     ? [MobileLoginGuard]
  //     : [MicrosoftLoginGuard],
  //   data: {
  //     breadcrumb: { skip: true }
  //   }
  // },
  /** END Task Management */

  /** Contract Portal */
  {
    path: 'contract-portal',
    loadChildren: () => import('@contract-portal/routes').then((m) => m.CONTRACT_PORTAL_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** End Contract Portal */
  {
    path: 'menu-config',
    component: MenuConfigComponent,
    canActivate: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent)
      ? [MobileLoginGuard]
      : [MicrosoftLoginGuard],
    data: {
      breadcrumb: 'Menu Config',
      required: Utils.filterRequiredByLink(menuItems, 'menu-config')
    }
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    data: { breadcrumb: 'Access Denied' }
  },
  {
    path: 'not-found',
    component: UrlNotFoundComponent
  },
  {
    path: '**',
    redirectTo: 'not-found'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled', paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor() {}
}
